import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TopBar from '../components/TopBar';
import { api } from '../api/client';

type Respuesta = {
  mensaje: string;
  respondido_por: string;
  creado_en: string;
  nuevo_estado: string | null;
};

type Imagen = { url: string };

type Reclamo = {
  id: number;
  nro_expediente: string;
  titulo: string;
  descripcion: string;
  estado: string;
  prioridad: string;
  categoria: string;
  categoria_icono: string;
  area: string;
  calle: string;
  nro_puerta: string;
  barrio: string;
  latitud: string | null;
  longitud: string | null;
  creado_en: string;
  actualizado_en: string;
  resuelto_en: string | null;
  calificacion: number | null;
  calificacion_cerrada: number;
  imagenes: Imagen[];
  respuestas: Respuesta[];
};

const STATUS: Record<string, { bg: string; text: string; label: string; icon: string }> = {
  pendiente:  { bg: 'bg-amber-50',  text: 'text-amber-700',  label: 'Pendiente',   icon: '⏳' },
  en_proceso: { bg: 'bg-blue-50',   text: 'text-blue-700',   label: 'En proceso',  icon: '🔄' },
  resuelto:   { bg: 'bg-green-50',  text: 'text-green-700',  label: 'Resuelto',    icon: '✅' },
  cerrado:    { bg: 'bg-green-50',  text: 'text-green-700',  label: 'Resuelto',    icon: '✅' },
  rechazado:  { bg: 'bg-red-50',    text: 'text-red-700',    label: 'Rechazado',   icon: '❌' },
};

const PRIORIDAD: Record<string, { bg: string; text: string; label: string }> = {
  baja:    { bg: 'bg-gray-100',   text: 'text-gray-600',   label: 'Baja' },
  media:   { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Media' },
  alta:    { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Alta' },
  urgente: { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Urgente' },
};

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('es-AR', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function DetalleReclamo() {
  const { id }  = useParams<{ id: string }>();
  const nav     = useNavigate();
  const [reclamo,       setReclamo]       = useState<Reclamo | null>(null);
  const [loading,       setLoading]       = useState(true);
  const [err,           setErr]           = useState<string | null>(null);
  const [rating,        setRating]        = useState(0);
  const [comentario,    setComentario]    = useState('');
  const [enviandoCalif, setEnviandoCalif] = useState(false);
  const [califErr,      setCalifErr]      = useState<string | null>(null);
  const [lightboxIdx,   setLightboxIdx]   = useState<number | null>(null);
  const mapDivRef    = useRef<HTMLDivElement>(null);
  const swipeStartX  = useRef(0);

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    try {
      const r = await api<Reclamo>(`/reclamos/${id}`);
      setReclamo(r);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      if (!silencioso) setLoading(false);
    }
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!reclamo) return;
    const lat = parseFloat(String(reclamo.latitud ?? ''));
    const lng = parseFloat(String(reclamo.longitud ?? ''));
    if (isNaN(lat) || isNaN(lng)) return;

    let map: L.Map | null = null;
    const t = setTimeout(() => {
      if (!mapDivRef.current) return;
      map = L.map(mapDivRef.current, {
        center: [lat, lng], zoom: 16,
        zoomControl: false, dragging: false,
        scrollWheelZoom: false, doubleClickZoom: false,
        touchZoom: false, keyboard: false,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors', maxZoom: 19,
      }).addTo(map);
      L.marker([lat, lng], {
        icon: L.divIcon({
          className: '',
          html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.45)"></div>',
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        }),
      }).addTo(map);
      setTimeout(() => map?.invalidateSize(), 200);
      setTimeout(() => map?.invalidateSize(), 700);
    }, 100);

    return () => { clearTimeout(t); map?.remove(); };
  }, [reclamo]);

  async function enviarCalificacion() {
    if (rating < 1) return setCalifErr('Tocá las estrellas para puntuar de 1 a 5.');
    setEnviandoCalif(true);
    setCalifErr(null);
    try {
      await api(`/reclamos?action=calificar&id=${id}`, {
        method: 'POST',
        body: JSON.stringify({ calificacion: rating, comentario: comentario.trim() }),
      });
      await cargar(true);
    } catch (e: any) {
      setCalifErr(e.message || 'No se pudo enviar la calificación.');
    } finally {
      setEnviandoCalif(false);
    }
  }

  async function noQuieroCalificar() {
    if (!confirm('¿No querés calificar? No recibirás más recordatorios sobre este reclamo.')) return;
    try {
      await api(`/reclamos?action=cerrar_calificacion&id=${id}`, { method: 'POST', body: JSON.stringify({}) });
      await cargar(true);
    } catch (e: any) {
      setErr(e.message);
    }
  }

  if (loading) {
    return (
      <>
        <TopBar title="Detalle del reclamo" back />
        <p className="text-center text-gray-400 mt-20">Cargando…</p>
      </>
    );
  }

  if (err || !reclamo) {
    return (
      <>
        <TopBar title="Detalle del reclamo" back />
        <p className="text-center text-red-500 mt-20 px-6">{err || 'Reclamo no encontrado'}</p>
      </>
    );
  }

  const st  = STATUS[reclamo.estado]    ?? STATUS.pendiente;
  const pr  = PRIORIDAD[reclamo.prioridad] ?? PRIORIDAD.media;
  const tienePin = !!(reclamo.latitud && reclamo.longitud);
  const puedeCalificar =
    ['resuelto', 'cerrado'].includes(reclamo.estado)
    && reclamo.calificacion == null
    && !reclamo.calificacion_cerrada;

  return (
    <>
      <TopBar title="Detalle del reclamo" back />

      {/* ── Lightbox ──────────────────────────────────────────── */}
      {lightboxIdx !== null && reclamo?.imagenes && (
        <div
          className="fixed inset-0 flex flex-col bg-gray-100"
          style={{ zIndex: 9999, paddingTop: 'env(safe-area-inset-top)' }}
        >
          {/* Cabecera estilo TopBar */}
          <div
            className="flex items-center justify-between px-4 flex-shrink-0"
            style={{ backgroundColor: '#dedede', borderBottom: '1px solid #c8c8c8', paddingTop: 12, paddingBottom: 12 }}
          >
            <button
              onClick={() => setLightboxIdx(null)}
              className="flex items-center gap-1 font-semibold text-sm"
              style={{ color: '#64748b' }}
            >
              ← Volver
            </button>
            {reclamo.imagenes.length > 1 && (
              <span className="text-sm font-medium" style={{ color: '#303030' }}>
                {lightboxIdx + 1} / {reclamo.imagenes.length}
              </span>
            )}
          </div>

          {/* Imagen — área swipeable */}
          <div
            className="flex-1 flex items-center justify-center px-5 py-5"
            onTouchStart={(e) => { swipeStartX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const dx = e.changedTouches[0].clientX - swipeStartX.current;
              if (dx > 50)  setLightboxIdx((p) => Math.max(0, (p ?? 0) - 1));
              if (dx < -50) setLightboxIdx((p) => Math.min(reclamo!.imagenes.length - 1, (p ?? 0) + 1));
            }}
          >
            <img
              src={reclamo.imagenes[lightboxIdx].url}
              alt=""
              className="max-w-full max-h-full object-contain rounded-2xl shadow-md"
              style={{ maxHeight: 'calc(100dvh - 140px)' }}
            />
          </div>

          {/* Puntitos + flechas */}
          {reclamo.imagenes.length > 1 && (
            <div className="flex justify-between items-center px-6 pb-6 flex-shrink-0">
              <button
                onClick={() => setLightboxIdx((p) => Math.max(0, (p ?? 0) - 1))}
                disabled={lightboxIdx === 0}
                className="w-11 h-11 rounded-full bg-white border border-app shadow-sm flex items-center justify-center text-gray-600 text-lg disabled:opacity-30 active:bg-gray-50"
              >←</button>
              <div className="flex gap-2 items-center">
                {reclamo.imagenes.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxIdx(i)}
                    className={`rounded-full transition-all ${i === lightboxIdx ? 'w-2.5 h-2.5 bg-brand-600' : 'w-2 h-2 bg-gray-300'}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setLightboxIdx((p) => Math.min(reclamo!.imagenes.length - 1, (p ?? 0) + 1))}
                disabled={lightboxIdx === reclamo.imagenes.length - 1}
                className="w-11 h-11 rounded-full bg-white border border-app shadow-sm flex items-center justify-center text-gray-600 text-lg disabled:opacity-30 active:bg-gray-50"
              >→</button>
            </div>
          )}
        </div>
      )}

      <main className="px-4 py-4 space-y-4 max-w-md mx-auto pb-24">

        {/* Expediente */}
        <div className="bg-gray-50 border border-app rounded-xl px-4 py-3 flex items-center justify-between">
          <span className="text-xs text-gray-500 font-semibold">Expediente</span>
          <span className="font-mono font-bold text-gray-900 text-sm tracking-wide">{reclamo.nro_expediente}</span>
        </div>

        {/* Chips estado + prioridad */}
        <div className="flex gap-2 flex-wrap">
          <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${st.bg} ${st.text} border-current border-opacity-25`}>
            {st.icon} {st.label}
          </span>
          <span className={`px-3 py-1 rounded-full border text-xs font-bold ${pr.bg} ${pr.text} border-current border-opacity-25`}>
            Prioridad {pr.label}
          </span>
        </div>

        {/* Calificar */}
        {puedeCalificar && (
          <div className="bg-white border border-amber-200 rounded-2xl p-4 space-y-3">
            <p className="font-extrabold text-gray-900 text-center text-sm">¿Cómo resolvimos tu reclamo?</p>
            <p className="text-gray-500 text-xs text-center">Tu opinión ayuda al municipio a mejorar.</p>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)}
                  className={`text-4xl leading-none transition-colors ${n <= rating ? 'text-amber-400' : 'text-gray-200'}`}
                >★</button>
              ))}
            </div>
            <textarea
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder="Contanos algo (opcional)"
              rows={3}
              maxLength={500}
              className="w-full bg-gray-50 border border-app rounded-xl px-3 py-2.5 text-sm text-gray-900 outline-none resize-none"
            />
            {califErr && <p className="text-red-600 text-xs">{califErr}</p>}
            <button
              onClick={enviarCalificacion}
              disabled={rating < 1 || enviandoCalif}
              className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-3 font-bold text-sm"
            >
              {enviandoCalif ? 'Enviando…' : 'Enviar calificación'}
            </button>
            <button onClick={noQuieroCalificar}
              className="w-full text-center text-gray-400 text-xs underline">
              No quiero calificar
            </button>
          </div>
        )}

        {/* Ya calificó */}
        {reclamo.calificacion != null && (
          <div className="bg-white border border-app rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-gray-500 font-semibold">Tu calificación</span>
            <span className="text-amber-400 text-lg tracking-wide">
              {'★'.repeat(reclamo.calificacion)}{'☆'.repeat(5 - reclamo.calificacion)}
            </span>
          </div>
        )}

        {/* Título + categoría */}
        <div>
          <h2 className="font-extrabold text-gray-900 text-sm leading-snug">{reclamo.titulo}</h2>
          {(reclamo.categoria || reclamo.area) && (
            <p className="text-gray-500 text-xs mt-1">
              {reclamo.categoria_icono} {reclamo.categoria}{reclamo.area ? ` · 🏛️ ${reclamo.area}` : ''}
            </p>
          )}
        </div>

        {/* Descripción */}
        <div className="bg-white border border-app rounded-xl p-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">📝 Descripción</p>
          <p className="text-sm text-gray-800 leading-relaxed">{reclamo.descripcion}</p>
        </div>

        {/* Ubicación */}
        {(reclamo.calle || reclamo.barrio || tienePin) && (
          <div className="bg-white border border-app rounded-xl p-4 space-y-3">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">📍 Ubicación del problema</p>
            {(reclamo.calle || reclamo.barrio) && (
              <p className="text-sm text-gray-800">
                {[reclamo.calle, reclamo.nro_puerta, reclamo.barrio ? `— Barrio ${reclamo.barrio}` : ''].filter(Boolean).join(' ')}
              </p>
            )}
            {tienePin && (
              <>
                <div
                  ref={mapDivRef}
                  style={{ height: 200, borderRadius: '0.75rem', overflow: 'hidden' }}
                />
                <a
                  href={`https://maps.google.com/?q=${reclamo.latitud},${reclamo.longitud}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center border border-app rounded-xl py-2.5 text-sm text-gray-600 font-medium"
                >
                  📍 Abrir en Google Maps →
                </a>
              </>
            )}
          </div>
        )}

        {/* Fotos */}
        {reclamo.imagenes?.length > 0 && (
          <div className="bg-white border border-app rounded-xl p-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">📷 Fotos adjuntas</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {reclamo.imagenes.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIdx(i)}
                  className="flex-shrink-0 active:scale-95 transition-transform"
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-28 h-20 rounded-xl object-cover border border-app bg-gray-100"
                    onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}


        {/* Fechas */}
        <div className="bg-white border border-app rounded-xl p-4 space-y-2">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">📅 Fechas</p>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Creado</span>
            <span className="text-gray-800 font-medium">{fmtDate(reclamo.creado_en)}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-500">Actualizado</span>
            <span className="text-gray-800 font-medium">{fmtDate(reclamo.actualizado_en)}</span>
          </div>
          {reclamo.resuelto_en && (
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Resuelto</span>
              <span className="text-gray-800 font-medium">{fmtDate(reclamo.resuelto_en)}</span>
            </div>
          )}
        </div>

        {/* Respuestas */}
        {reclamo.respuestas?.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">💬 Respuestas del municipio</p>
            <div className="space-y-3">
              {reclamo.respuestas.map((r, i) => (
                <div key={i} className="bg-white border border-app rounded-xl p-4">
                  <div className="flex gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1 ${
                      i === reclamo.respuestas.length - 1 ? 'bg-green-500' : 'bg-brand-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 leading-relaxed">{r.mensaje}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500 font-semibold">{r.respondido_por || 'Sistema Municipal'}</span>
                        <span className="text-xs text-gray-400">
                          {new Date(r.creado_en).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {r.nuevo_estado && STATUS[r.nuevo_estado] && (
                        <div className={`mt-2 rounded-lg px-3 py-1.5 text-xs font-bold inline-block ${STATUS[r.nuevo_estado].bg} ${STATUS[r.nuevo_estado].text}`}>
                          {STATUS[r.nuevo_estado].icon} Estado actualizado: {STATUS[r.nuevo_estado].label}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sin respuestas */}
        {(!reclamo.respuestas || reclamo.respuestas.length === 0) && reclamo.estado === 'pendiente' && (
          <div className="bg-white border border-app rounded-2xl p-8 text-center">
            <p className="text-4xl mb-3">⏳</p>
            <p className="text-gray-500 text-sm leading-relaxed">
              Tu reclamo está siendo revisado.<br />Te notificaremos cuando haya novedades.
            </p>
          </div>
        )}

        <button
          onClick={() => nav('/reclamos')}
          className="w-full border border-app rounded-xl py-3 text-sm text-gray-500 font-medium"
        >
          ← Volver a mis reclamos
        </button>

      </main>
    </>
  );
}
